import CardTitle from 'common/components/CardTitle.tsx'
import {Button, Card, Col, DatePicker, Row, Spin} from 'antd'
import {useUserContext} from 'common/context/UserContext.tsx'
import {Format, formatCompact, toTimezone} from 'common/util/Util.tsx'
import dayjs from 'dayjs'
import React, {useMemo} from 'react'
import {PerformanceData} from './UseDashboardData.ts'

const {RangePicker} = DatePicker;

interface Props {
    performanceData?: PerformanceData;
    activeFilter: [dayjs.Dayjs, dayjs.Dayjs, boolean, boolean]; // [start, end, isHourly, isManual]
    setActiveFilter: (value: [dayjs.Dayjs, dayjs.Dayjs, boolean, boolean]) => void;
    loading?: boolean;
}

const DashboardHeader: React.FC<Props> = ({performanceData, activeFilter, setActiveFilter, loading}) => {
    const {user} = useUserContext();
    const { colorPalette } = useUserContext()
    const tz = user?.timezoneId;

    const predefinedFilters = useMemo(() => [
        {
            label: 'Today',
            range: () => {
                const now = toTimezone(dayjs(), tz);
                return [now.startOf('day'), now.endOf('day'), true, false] as [dayjs.Dayjs, dayjs.Dayjs, boolean, boolean];
            }
        },
        {
            label: 'Yesterday',
            range: () => {
                const yesterday = toTimezone(dayjs().subtract(1, 'day'), tz);
                return [yesterday.startOf('day'), yesterday.endOf('day'), true, false];
            }
        },
        {
            label: 'Last 7 Days',
            range: () => {
                const end = toTimezone(dayjs(), tz).endOf('day');
                const start = end.subtract(6, 'day').startOf('day');
                return [start, end, false, false];
            }
        },
        {
            label: 'Last 28 Days',
            range: () => {
                const end = toTimezone(dayjs(), tz).endOf('day');
                const start = end.subtract(27, 'day').startOf('day');
                return [start, end, false, false];
            }
        },
        {
            label: 'Last Month',
            range: () => {
                const now = toTimezone(dayjs(), tz);
                const start = now.subtract(1, 'month').startOf('month');
                const end = now.subtract(1, 'month').endOf('month');
                return [start, end, false, false];
            }
        },
        {
            label: 'This Month',
            range: () => {
                const now = toTimezone(dayjs(), tz);
                const start = now.startOf('month');
                const end = now.endOf('month');
                return [start, end, false, false];
            }
        }
    ], [tz]);

    const isSameRange = (
        a: [dayjs.Dayjs, dayjs.Dayjs, boolean, boolean],
        b: [dayjs.Dayjs, dayjs.Dayjs, boolean, boolean]
    ) => a[0].isSame(b[0], 'day') && a[1].isSame(b[1], 'day');

    const hasNetCost = performanceData?.costNetSum !== undefined;

    const stats = [
        {title: hasNetCost ? 'Cost Net' : 'Cost', value: hasNetCost ? performanceData?.costNetSum : performanceData?.costSum, format: Format.Cost},
        {title: 'Impressions', value: performanceData?.impressionsSum, format: Format.Int},
        {title: 'Clicks', value: performanceData?.clicksSum, format: Format.Int},
        {title: 'CTR', value: performanceData?.ctrAvg, format: Format.Percent},
        {title: hasNetCost ? 'Profit' : 'Events', value: hasNetCost ? performanceData?.profitSum : performanceData?.trackersSum, format: hasNetCost ? Format.Cost : Format.Int},
        {title: 'eCPM', value: performanceData?.ecpmAvg, format: Format.Cost}
    ];

    return (
        <Card style={{background: '#FFFFFF', border: 'transparent'}} bodyStyle={{padding: 24}}>
            <CardTitle title="Dashboard" style={{marginBottom: '32px'}}/>

            <Row gutter={[2, 16]} style={{marginBottom: 24}} wrap justify="start">
                {predefinedFilters.map(({label, range}) => {
                    const rangeValue = range();
                    const isActive = isSameRange(
                        activeFilter,
                        rangeValue as [dayjs.Dayjs, dayjs.Dayjs, boolean, boolean],
                    );

                    return (
                        <Col key={label}>
                            <Button
                                style={{background: isActive ? colorPalette['--color-primary-3'] : '#FFFFFF', paddingLeft: 8, paddingRight: 8}}
                                className="responsive-button"
                                type={isActive ? 'primary' : 'text'}
                                onClick={() => {
                                    setActiveFilter(rangeValue as [dayjs.Dayjs, dayjs.Dayjs, boolean, boolean]);
                                }}
                            >
                                <span style={{color: '#000'}}>{label}</span>
                            </Button>
                        </Col>
                    );
                })}
                <Col style={{marginLeft: 8}}>
                    <RangePicker
                        value={[activeFilter[0], activeFilter[1]] as any}
                        allowClear={false}
                        onChange={(dates) => {
                            if (dates && dates[0] && dates[1]) {
                                const start = toTimezone(dates[0], tz).startOf('day');
                                const end = toTimezone(dates[1], tz).endOf('day');
                                const diffDays = end.diff(start, 'day') + 1;
                                setActiveFilter([start, end, diffDays <= 2, true]);
                            }
                        }}
                        aria-label="Select dashboard date range"
                    />
                </Col>
            </Row>

            <Row gutter={[16, 16]} justify="space-between">
                {loading ? <Spin size="large" style={{
                        display: 'block',
                        margin: '20px auto',
                        minHeight: '100px',
                        alignContent: 'center'
                    }}/> :
                    stats.map((stat) => (
                        <Col flex="1 0 0%" key={stat.title}>
                            <Card
                                style={{
                                    // background: '#f0f5ff',
                                    background: colorPalette['--color-primary-3'],
                                    borderRadius: 4,
                                    textAlign: 'left',
                                    height: '100%',
                                    paddingLeft: 12,
                                    paddingTop: 8,
                                    paddingBottom: 16,
                                    border: 'transparent'
                                }}
                                bodyStyle={{padding: 0, height: '100%'}}
                            >
                                <div style={{color: '#0D1A43'}}>{stat.title}</div>

                                <div style={{fontSize: 24, fontWeight: 700, color: '#1f2937', paddingTop: 12}}>
                                    {formatCompact(stat.format, stat.value)}
                                </div>
                            </Card>
                        </Col>
                    ))}
            </Row>
        </Card>
    );
};

export default DashboardHeader;
